import { memo, useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addUserMessage, messagesSelectors, setChatStatus } from 'store/messages'
import { settingsSelectors } from 'store/settings'
import { createUserMessage } from 'utils/helpers'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Icon } from 'components/ui/Icon'
import { ComposerTextarea } from 'components/ui/ComposerTextarea'
import { ComposerAttachments, ComposerItem } from 'components/ui/ComposerAttachments'
import { askBotAnswer } from 'api/index'
import { sentMessageToOperator } from 'api/chats'
import { deleteChatAttachment, uploadChatAttachment } from 'api/chat-attachments'
import { CHAT_ATTACHMENT_ACCEPT } from 'utils/chat-attachments'
import { WidgetFileUpload } from 'components/WidgetFileUpload'
import { Spinner } from 'components/ui/Spinner'
import { showErrorToast } from 'utils/toast'
import { ChatAttachment, ChatAttachmentStatusEnum } from 'types/chat-attachment'
import { useChatAttachmentParsing } from 'hooks/useChatAttachmentParsing'

// One file in the composer. Operator files stay 'ready' (uploaded only on send via UploadService).
// Bot files go 'uploading' (HTTP) -> 'processing' (server parsing) -> 'ready'.
type UploadStatus = 'uploading' | 'processing' | 'ready'

type ComposerUpload = {
  id: string
  file: File
  status: UploadStatus
  attachment?: ChatAttachment
}

const ChatTextInputComponent = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [uploads, setUploads] = useState<ComposerUpload[]>([])
  const [userInput, setUserInput] = useState('')
  const idRef = useRef(0)
  const chatId = useSelector(messagesSelectors.chatId)
  const userTypingPlaceholder = useSelector(messagesSelectors.userTypingPlaceholder)
  const userTyping = useSelector(messagesSelectors.userTyping)
  const isInOperatorMode = useSelector(messagesSelectors.currentChatIsInOperatorMode)
  const disableFields = useSelector(messagesSelectors.disableFieldsTicketClosed)
  const isSending = useSelector(messagesSelectors.currentChatSending)
  const chatAttachmentsEnabled = useSelector(settingsSelectors.chatAttachmentsEnabled)

  const attachmentsEnabled = isInOperatorMode || chatAttachmentsEnabled
  const isLoadingAttachments = uploads.some((upload) => upload.status !== 'ready')

  // Operator: keep the file locally, uploaded on send. Bot: upload now, then await parsing.
  const onFilesAdded = useCallback(
    (newFiles: File[]) => {
      newFiles.forEach((file) => {
        const id = String(idRef.current++)
        if (isInOperatorMode) {
          setUploads((prev) => [...prev, { id, file, status: 'ready' }])
          return
        }
        if (!chatId) return
        setUploads((prev) => [...prev, { id, file, status: 'uploading' }])
        uploadChatAttachment(file, chatId)
          .then((attachment) => {
            const status: UploadStatus = attachment.status === ChatAttachmentStatusEnum.READY ? 'ready' : 'processing'
            setUploads((prev) => prev.map((upload) => (upload.id === id ? { ...upload, status, attachment } : upload)))
          })
          .catch((e) => {
            console.error({ e })
            showErrorToast(t('chatAttachmentUploadError'))
            setUploads((prev) => prev.filter((upload) => upload.id !== id))
          })
      })
    },
    [isInOperatorMode, chatId, t],
  )

  // Removing a bot attachment deletes it server-side (privacy); operator files are only local.
  const onRemove = useCallback((id: string) => {
    setUploads((prev) => {
      const target = prev.find((upload) => upload.id === id)
      if (target?.attachment) {
        deleteChatAttachment(target.attachment._id).catch((e) => console.error({ e }))
      }
      return prev.filter((upload) => upload.id !== id)
    })
  }, [])

  const processingIds = uploads
    .filter((upload) => upload.status === 'processing' && upload.attachment)
    .map((upload) => upload.attachment!._id)

  useChatAttachmentParsing(processingIds, {
    onReady: (attachmentId) =>
      setUploads((prev) =>
        prev.map((upload) => (upload.attachment?._id === attachmentId ? { ...upload, status: 'ready' } : upload)),
      ),
    onFailed: (attachmentId) => {
      showErrorToast(t('chatAttachmentFailed'))
      setUploads((prev) => prev.filter((upload) => upload.attachment?._id !== attachmentId))
    },
  })

  const handleSubmit = useCallback(async () => {
    const sendingChatId = chatId
    if (!sendingChatId) return
    const question = userInput.trim()
    if (!question || isLoadingAttachments) return

    try {
      if (isInOperatorMode) {
        const filesToSend = uploads.map((upload) => upload.file)
        setUserInput('')
        setUploads([])
        dispatch(setChatStatus({ chatId: sendingChatId, status: 'sending' }))
        try {
          await sentMessageToOperator({ message: question, files: filesToSend })
        } finally {
          dispatch(setChatStatus({ chatId: sendingChatId, status: 'idle' }))
        }
        return
      }

      // Bot: attachments are already uploaded and parsed; just reference them. Clearing the list
      // does NOT delete them — they are now committed to the message.
      const attachments = uploads.map((upload) => upload.attachment).filter(Boolean) as ChatAttachment[]
      setUserInput('')
      setUploads([])
      const { message, _id: questionId, senderId, chatSenderRole } = createUserMessage(question)
      dispatch(
        addUserMessage({
          chatId: sendingChatId,
          _id: questionId,
          message,
          senderId,
          chatSenderRole,
          attachments: attachments.length ? attachments : undefined,
        }),
      )
      // askBotAnswer owns the bot status (awaitingBot -> idle); we only add the message.
      await askBotAnswer({ question, questionId, attachmentIds: attachments.map((attachment) => attachment._id) })
    } catch (e) {
      console.error({ e })
    }
  }, [dispatch, userInput, isInOperatorMode, uploads, isLoadingAttachments, chatId])

  const items: ComposerItem[] = uploads.map((upload) => ({
    id: upload.id,
    name: upload.file.name,
    size: upload.file.size,
    file: upload.file,
    loading: upload.status !== 'ready',
    uploading: upload.status === 'uploading',
  }))

  const sendDisabled = disableFields || isSending || isLoadingAttachments || !userInput.trim()

  return (
    <div
      className="tw-flex tw-w-full tw-flex-col tw-rounded-[12px] tw-p-1"
      style={{
        background: 'var(--input-bg)',
        border: '1px solid var(--border-default)',
      }}
    >
      <ComposerAttachments items={items} onRemove={onRemove} disabled={isSending} />

      <div className="tw-flex tw-min-h-[40px] tw-w-full tw-items-end">
        {attachmentsEnabled && (
          <WidgetFileUpload
            disable={disableFields || isSending}
            accept={isInOperatorMode ? undefined : CHAT_ATTACHMENT_ACCEPT}
            onFilesAdded={onFilesAdded}
          />
        )}
        <ComposerTextarea
          value={userInput}
          onChange={setUserInput}
          onSubmit={() => handleSubmit().catch(console.error)}
          placeholder={t(userTypingPlaceholder)}
          disabled={disableFields}
          readOnly={!userTyping}
          className={clsx('tw-text-strong', userTyping ? 'tw-cursor-default' : 'tw-cursor-not-allowed')}
        />
        <button
          type="submit"
          disabled={sendDisabled}
          className={clsx(
            'tw-flex tw-items-center tw-justify-center tw-w-[40px] tw-h-[40px] tw-rounded-[8px] tw-shrink-0',
            !sendDisabled ? 'tw-cursor-default' : 'tw-cursor-not-allowed',
          )}
          style={{
            background: 'linear-gradient(167deg, var(--primary-gradient-start), var(--primary-gradient-end))',
          }}
          onClick={(e) => {
            e.preventDefault()
            handleSubmit().catch(console.error)
          }}
        >
          {isSending ? (
            <Spinner color="white" />
          ) : (
            <Icon icon={['far', 'paper-plane-top']} className="tw-h-5 tw-w-5" style={{ color: 'white' }} />
          )}
        </button>
      </div>
    </div>
  )
}

export const ChatTextInput = memo(ChatTextInputComponent)
ChatTextInput.displayName = 'ChatTextInput'
